import { PiArrowBendUpLeft, PiArrowSquareOut, PiPhone } from 'react-icons/pi';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { TemplateComposerViewProps } from '../../../types/templates/template-composer.types';
import { renderTemplateSection } from '../../../utils/template-composer.util';
import WhatsAppText from '../messages/whatsapp-text';

/**
 * Inert recipient-facing preview; approved actions are visible without being executable.
 * @param props - Current feature state and consumer-owned callbacks.
 * @returns The corresponding composer surface.
 */
export const TemplatePreview = ({
  definition,
  values,
}: TemplateComposerViewProps) => {
  const { t } = useI18n();
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div>
        <Text weight="medium">{t('whatsappTemplates.preview')}</Text>
        <Text size="tiny" color="secondary">
          {t('whatsappTemplates.previewHelp')}
        </Text>
      </div>
      <div className="rounded-2xl border border-secondary-100 bg-[#eff3ef] p-4 sm:p-5">
        <div className="ml-auto max-w-sm overflow-hidden rounded-xl rounded-tr-sm border border-[#cee4c8] bg-[#e4f8dc] shadow-sm">
          <div className="space-y-2 break-words px-4 py-3">
            {definition.header && (
              <Text
                size="small"
                weight="semibold"
                className="whitespace-pre-wrap"
                disableAutoLink
              >
                {renderTemplateSection(definition.header, 'header', values)}
              </Text>
            )}
            <WhatsAppText
              text={renderTemplateSection(definition.body, 'body', values)}
            />
            {definition.footer && (
              <Text
                size="tiny"
                color="secondary"
                className="whitespace-pre-wrap"
                disableAutoLink
              >
                {definition.footer}
              </Text>
            )}
          </div>
          {definition.buttons.map((button, index) => (
            <div
              key={index}
              className="flex items-center justify-center gap-2 border-t border-[#cee4c8] px-3 py-3 text-center text-[#1f6b83]"
            >
              {button.type === 'url' ? (
                <PiArrowSquareOut aria-hidden />
              ) : button.type === 'phone_number' ? (
                <PiPhone aria-hidden />
              ) : (
                <PiArrowBendUpLeft aria-hidden />
              )}
              <Text as="span" size="small" weight="medium">
                {button.text}
              </Text>
            </div>
          ))}
        </div>
      </div>
      {definition.buttons
        .filter((button) => button.destination)
        .map((button, index) => (
          <div key={index} className="min-w-0">
            <Text size="tiny" color="secondary" weight="medium">
              {button.text}
            </Text>
            <Text
              size="tiny"
              color="secondary"
              className="break-all"
              disableAutoLink
            >
              {button.fieldKey
                ? button.destination?.replace(
                    '{{1}}',
                    () => values[button.fieldKey ?? ''] || '{{1}}'
                  )
                : button.destination}
            </Text>
          </div>
        ))}
      <Text size="tiny" color="secondary">
        {t('whatsappTemplates.previewDisclaimer')}
      </Text>
    </div>
  );
};
